import React from 'react';
import { Delete } from '@insurancemenu/imenu-components-lib/dist/icons';
import { DropdownOptionType, OptionsAlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { IMBaseColors } from '@insurancemenu/imenu-components-lib/dist/utils/colors';
import { observer } from 'mobx-react-lite';
import { EmployeeActionUiPresenter } from 'store/modules/add-employees/employeeDelegate';

import { TableActionsDropdown } from 'components/common/general/TableActionsDropdown';
import ModalDelegate from 'store/general/modalDelegate';
import { GeneralEmployeeAction } from 'store/modules/add-employees/types';
import { AVAILABLE_ACTIONS_TEST_IDS } from '../constants';

type RowActionIcons = Partial<Record<string, React.FC>>;

interface AvailableActionsProps {
  availableActions: EmployeeActionUiPresenter[];
  warningModals: Record<string, ModalDelegate>;
  /** Flow-specific icons; remove uses a default icon from {@link DEFAULT_ACTION_ICONS}. */
  actionIcons?: RowActionIcons;
}

function getOptionColor(actionType: string): IMBaseColors | undefined {
  if (actionType === String(GeneralEmployeeAction.REMOVE_EMPLOYEE)) {
    return 'red';
  }
  return undefined;
}

/** String-keyed so arbitrary flow action ids can be resolved without casting. */
const DEFAULT_ACTION_ICONS: RowActionIcons = {
  [GeneralEmployeeAction.REMOVE_EMPLOYEE]: Delete,
};

function resolveActionIcon(type: string, actionIcons?: RowActionIcons): React.FC | undefined {
  return actionIcons?.[type] ?? DEFAULT_ACTION_ICONS[type];
}

function mapActionToDropdownOption(
  action: EmployeeActionUiPresenter,
  actionIcons: RowActionIcons | undefined,
  warningModal?: ModalDelegate,
): DropdownOptionType {
  const actionId = String(action.type);
  return {
    value: actionId,
    icon: resolveActionIcon(actionId, actionIcons),
    label: action.name,
    'data-test-id': `${AVAILABLE_ACTIONS_TEST_IDS.OPTION_PREFIX}${actionId}`,
    customColor: getOptionColor(actionId),
    onOptionClick: (): void => {
      if (warningModal) {
        warningModal.open();
        return;
      }
      void action.handler();
    },
  };
}

export const AvailableActions: React.FC<AvailableActionsProps> = observer(
  ({ availableActions, warningModals, actionIcons }) => {
    return (
      <TableActionsDropdown
        name="More Options"
        options={availableActions.map((action) =>
          mapActionToDropdownOption(action, actionIcons, warningModals[action.type]),
        )}
        optionsWidth="25rem"
        optionsAlignment={OptionsAlignmentTypes.BOTTOM_LEFT}
        data-test-id={AVAILABLE_ACTIONS_TEST_IDS.TABLE_ACTIONS_DROPDOWN}
      />
    );
  },
);
